import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";
import { Reveal } from "@/components/reveal";
import { Section, SectionTitle } from "@/components/section";
import { ParallaxImage } from "@/components/parallax-image";
import { ImageMarquee } from "@/components/image-marquee";
import { AnimatedGallery } from "@/components/animated-gallery";
import { CtaBand } from "@/components/cta-band";
import { lcpPreload, imgSizes } from "@/lib/images";
import { ResponsiveImg } from "@/components/responsive-img";
import {
  BUSINESS,
  REASONS,
  PROCESS,
  SERVICES,
  REVIEWS,
  STUDIO,
} from "@/lib/site-data";



export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Magnolia House Custom Picture Framing | Fuquay-Varina, NC" },
      {
        name: "description",
        content:
          "Custom, archival picture framing in Fuquay-Varina by Certified Professional Framer Shirley Hunsberger. Jerseys, diplomas, flags and heirlooms framed since 1989.",
      },
      {
        property: "og:title",
        content: "Magnolia House Custom Picture Framing | Fuquay-Varina, NC",
      },
      {
        property: "og:description",
        content:
          "Thirty-five years of certified, by-appointment custom framing in Fuquay-Varina, NC. Call Shirley at (919) 552-6802.",
      },
    ],
    links: [lcpPreload("hero-frames")],
  }),
  component: Home,
});

function Home() {
  return (
    <>
      {/* Hero */}
      <header className="night relative isolate flex min-h-[92vh] items-end overflow-hidden pb-20 pt-40">
        <ParallaxImage
          name="hero-frames"
          priority
          alt="Frame samples on the wall at Magnolia House in Fuquay-Varina"
          speed={0.35}
          className="absolute inset-0 -z-10"
        />
        <div className="hero-dim absolute inset-0 -z-10" />

        <div className="shell">
          <p className="eyebrow-light animate-rise">
            Fuquay-Varina, North Carolina · Established {BUSINESS.since}
          </p>
          <h1 className="display mt-6 max-w-4xl text-[15vw] leading-[0.9] sm:text-[9vw] lg:text-[7.5rem]">
            The things you
            <br />
            can't <span className="amber-text italic">replace.</span>
          </h1>
          <p
            className="animate-rise mt-8 max-w-lg text-[15px] leading-relaxed text-muted-foreground"
            style={{ animationDelay: "170ms" }}
          >
            Custom picture framing designed one piece at a time by Certified
            Professional Framer Shirley Hunsberger — archival materials, honest
            advice, and thirty-five years of steady hands.
          </p>
          <div
            className="animate-rise mt-10 flex flex-wrap items-center gap-4"
            style={{ animationDelay: "250ms" }}
          >
            <a
              href={BUSINESS.phoneHref}
              className="btn-ghost text-[var(--amber-warm)]"
            >
              <Phone className="size-3.5" />
              {BUSINESS.phone}
            </a>
            <Link to="/gallery" className="btn-ghost text-[oklch(0.96_0.008_240)]">
              See the work
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Marquee of finished work */}
      <div className="night pb-20">
        <ImageMarquee
          images={[
            "framed-jersey-medal",
            "framed-guitar",
            "framed-flag",
            "shadow-box",
            "framed-medals",
            "mat-designs",
            "framed-varsity",
            "framed-coins",
          ]}
        />
      </div>

      {/* Shirley */}
      <Section className="content-auto">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="plate">
              <ResponsiveImg
                name="shirley-photo"
                sizes={imgSizes.portrait}
                alt="Shirley Hunsberger, Certified Professional Framer"
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full object-cover"
              />

            </div>
          </Reveal>
          <div>
            <SectionTitle
              eyebrow="Meet the framer"
              title={
                <>
                  Thirty-five years of
                  <br />
                  <span className="amber-text italic">certified</span> craft.
                </>
              }
              lead="Shirley Hunsberger holds a Master's Certification through the Professional Picture Framers Association. She has designed and built frames for her Fuquay-Varina neighbours since 1989 — every consultation is with her, never a counter clerk."
            />
            <Reveal delay={120} className="mt-10 grid grid-cols-2 gap-6">
              {REASONS.map((r) => (
                <div key={r.stat} className="border-t border-border pt-4">
                  <p className="display text-4xl text-primary">{r.stat}</p>
                  <p className="mt-2 text-sm font-medium">{r.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {r.body}
                  </p>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Services preview */}
      <Section tone="mist" className="content-auto">
        <SectionTitle
          eyebrow="What we do"
          title="Framed for the way you'll keep it."
          lead="From a single photograph to a shadow box holding a jersey, a flag, or a christening gown — designed for your piece, not pulled off a shelf."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {SERVICES.slice(0, 6).map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 90}>
              <Link to="/services" className="plate group block">
                <div className="aspect-[5/4] overflow-hidden">
                  <ResponsiveImg
                    name={s.image}
                    sizes={imgSizes.third}
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="display text-2xl">{s.title}</h3>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                    {s.body}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-primary transition-transform duration-500 group-hover:translate-x-1">
                    Learn more <ArrowRight className="size-3.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120} className="mt-12">
          <Link to="/services" className="btn-primary">
            All services <ArrowRight className="size-3.5" />
          </Link>
        </Reveal>
      </Section>

      {/* Process */}
      <Section tone="night" className="content-auto">
        <SectionTitle
          light
          eyebrow="How it works"
          title="Five unhurried steps."
          lead="No pressure, no upsell, no surprises — just a design conversation and a quote you approve before anything is ordered."
        />
        <ol className="mt-14 grid gap-8 md:grid-cols-3 lg:grid-cols-5">
          {PROCESS.map((p, i) => (
            <Reveal as="li" key={p.step} delay={i * 80}>
              <p className="display text-5xl text-[var(--amber-warm)]">{p.step}</p>
              <h3 className="mt-4 text-sm font-semibold uppercase tracking-[0.16em]">
                {p.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {p.body}
              </p>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* The Studio */}
      <Section id="studio" className="content-auto">
        <SectionTitle
          eyebrow="The studio"
          title="A working frame shop."
          lead="Magnolia House is a real workshop in Fuquay-Varina — hundreds of frame corner samples on the wall, racks of mat board, and a full cutting and assembly bench where every piece is built by hand."
        />
        <div className="mt-14">
          <AnimatedGallery items={STUDIO} />
        </div>
      </Section>

      <Section className="content-auto">

        <SectionTitle eyebrow="In their words" title="Neighbours who trusted us with the irreplaceable." />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {REVIEWS.slice(0, 2).map((r, i) => (
            <Reveal key={r.name} delay={i * 100}>
              <figure className="h-full rounded-xl bg-card p-8 shadow-[var(--shadow-soft)]">
                <blockquote className="display text-2xl leading-snug">
                  “{r.quote}”
                </blockquote>
                <figcaption className="mt-6 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {r.name} · <span className="text-[var(--terracotta)]">{r.source}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120} className="mt-12">
          <Link to="/reviews" className="btn-primary">
            Read more reviews <ArrowRight className="size-3.5" />
          </Link>
        </Reveal>
      </Section>

      <CtaBand />
    </>
  );
}
